import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import type { KnownKinds } from '@ipen-portal/plugin-common';

import { stringifyEntityRef } from '@backstage/catalog-model';
import { normalizeEntityRef } from '@ipen-portal/plugin-common';
import { useAsync } from 'react-use';

import { useGetEntityByRefCallback } from './useGetEntityByRefCallback';

/**
 * Fetch a entity by its reference.
 *
 * @param entityRef The reference of the entity to fetch. Can be a string or a CompoundEntityRef.
 * @param options The to do additional filtering
 */
export const useGetEntityByRef = <T extends Entity>(
  entityRef: CompoundEntityRef | null | string | undefined,
  options?: {
    fallbackKind?: KnownKinds;
    predicate?: (entity: Entity) => entity is T;
  },
) => {
  const callback = useGetEntityByRefCallback();
  const { fallbackKind, predicate } = options ?? {};
  let resolvedEntityRef: null | string = null;
  if (!entityRef) {
    resolvedEntityRef = null;
  } else if (typeof entityRef === 'string') {
    resolvedEntityRef = normalizeEntityRef(entityRef, fallbackKind);
  } else {
    resolvedEntityRef = stringifyEntityRef(entityRef);
  }
  return useAsync(async () => {
    if (!resolvedEntityRef) {
      return;
    }
    const result = await callback(resolvedEntityRef, {});
    if (predicate && result) {
      return predicate(result) ? result : undefined;
    }
    return result as T;
  }, [callback, resolvedEntityRef, predicate]);
};
